import { Suspense, lazy, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import BetaBanner from "@/components/beta-banner";
import PageTransition from "@/components/page-transition";
import CookieConsentBanner from "@/components/cookie-consent-banner";
import { trackPageview } from "@/lib/analytics";
import { Sentry, setSentryTag } from "@/lib/sentry";
import Home from "@/pages/home";
import NotFound from "@/pages/not-found";

const BuyProperty = lazy(() => import("@/pages/buy"));
const RentProperty = lazy(() => import("@/pages/rent"));
const PostProperty = lazy(() => import("@/pages/post"));
const Vendors = lazy(() => import("@/pages/vendors"));
const OfferChain = lazy(() => import("@/pages/tools/offerchain"));
const SmartBid = lazy(() => import("@/pages/tools/smartbid"));
const PropGauge = lazy(() => import("@/pages/tools/propgauge"));
const DynamicCity = lazy(() => import("@/pages/cities/dynamic-city"));
const Valuation = lazy(() => import("@/pages/valuation"));
const Compare = lazy(() => import("@/pages/compare"));
const Methodology = lazy(() => import("@/pages/methodology"));
const ValuationShared = lazy(() => import("@/pages/valuation-shared"));
const History = lazy(() => import("@/pages/history"));
const Admin = lazy(() => import("@/pages/admin"));
const SeoHealth = lazy(() => import("@/pages/admin/seo-health"));
const AdminPrivacy = lazy(() => import("@/pages/admin-privacy"));
const Contact = lazy(() => import("@/pages/contact"));
const GuidesIndex = lazy(() => import("@/pages/guides/index"));
const GuideDetail = lazy(() => import("@/pages/guides/detail"));
const StyleGuide = lazy(() => import("@/pages/style-guide"));

const PrivacyPolicy = lazy(() => import("@/pages/legal/privacy"));
const TermsPage = lazy(() => import("@/pages/legal/terms"));
const CookiePolicyPage = lazy(() => import("@/pages/legal/cookies"));
const RefundPolicy = lazy(() => import("@/pages/legal/refund"));
const DisclaimerPage = lazy(() => import("@/pages/legal/disclaimer"));
const GrievancePage = lazy(() => import("@/pages/legal/grievance"));
const SecurityPage = lazy(() => import("@/pages/legal/security"));
const PrivacyRequests = lazy(() => import("@/pages/privacy/requests"));
const PrivacyContact = lazy(() => import("@/pages/privacy/contact"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-9 w-9 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

function Router() {
  const [location] = useLocation();
  useEffect(() => {
    trackPageview(location);
    setSentryTag("route", location);
  }, [location]);
  return (
    <Suspense fallback={<RouteFallback />}>
      <PageTransition>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/buy" component={BuyProperty} />
        <Route path="/rent" component={RentProperty} />
        <Route path="/post" component={PostProperty} />
        <Route path="/vendors" component={Vendors} />
        <Route path="/tools/offerchain" component={OfferChain} />
        <Route path="/tools/smartbid" component={SmartBid} />
        <Route path="/tools/propgauge" component={PropGauge} />
        <Route path="/cities/:city/:child">
          {(params) => <DynamicCity citySlug={params.city} childSlug={params.child} />}
        </Route>
        <Route path="/cities/:city">
          {(params) => <DynamicCity citySlug={params.city} />}
        </Route>
        <Route path="/valuation" component={Valuation} />
        <Route path="/compare" component={Compare} />
        <Route path="/compare/:a/vs/:b" component={Compare} />
        <Route path="/how-it-works/methodology" component={Methodology} />
        <Route path="/v/:slug" component={ValuationShared} />
        <Route path="/history" component={History} />
        <Route path="/guides" component={GuidesIndex} />
        <Route path="/guides/:slug">
          {(params) => <GuideDetail slug={params.slug} />}
        </Route>
        <Route path="/admin/seo/health" component={SeoHealth} />
        <Route path="/admin/privacy" component={AdminPrivacy} />
        <Route path="/admin" component={Admin} />
        {import.meta.env.DEV && <Route path="/style-guide" component={StyleGuide} />}
        <Route path="/contact" component={Contact} />

        <Route path="/legal/privacy" component={PrivacyPolicy} />
        <Route path="/legal/terms" component={TermsPage} />
        <Route path="/legal/cookies" component={CookiePolicyPage} />
        <Route path="/legal/refund" component={RefundPolicy} />
        <Route path="/legal/disclaimer" component={DisclaimerPage} />
        <Route path="/legal/grievance" component={GrievancePage} />
        <Route path="/legal/security" component={SecurityPage} />
        <Route path="/privacy/requests" component={PrivacyRequests} />
        <Route path="/privacy/contact" component={PrivacyContact} />

        <Route component={NotFound} />
      </Switch>
      </PageTransition>
    </Suspense>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: 24 }}>Something went wrong. Please refresh.</div>}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BetaBanner />
          <Toaster />
          <Router />
          <CookieConsentBanner />
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
